import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle, FileDown, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useLivePoll } from "@/hooks/useLivePoll";
import ReportDocumentView from "@/components/ReportDocumentView";
import EmailModal from "@/components/EmailModal";
import { downloadReportPdf } from "@/lib/reportPdf";

/**
 * BreakdownDetailPage — full-page render of an asset breakdown record
 * (Josh Feb 23: "when you click on that breakdown / handover it opens up
 * a full page so you can easily see it"). Uses the shared certificate
 * layout via <ReportDocumentView type="breakdown"/> so the visual style
 * matches PPF / Hazard / Handover exactly.
 *
 * Backend: fetches from /api/breakdowns/{id}. Falls back to scanning the
 * /api/breakdowns list if the singular endpoint 404s (older backends).
 */
export default function BreakdownDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [bd, setBd] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  const reload = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/breakdowns/${id}`);
      setBd(data);
    } catch (ex) {
      // Fallback: some backends only expose the list endpoint. Scan it.
      if (ex?.response?.status === 404 || ex?.response?.status === 405) {
        try {
          const { data } = await api.get("/breakdowns");
          const arr = Array.isArray(data) ? data : data?.items || [];
          const hit = arr.find((x) => (x.id || x._id) === id);
          if (hit) { setBd(hit); return; }
          setErr("Breakdown not found.");
        } catch (ex2) { setErr(apiError(ex2)); }
      } else {
        setErr(apiError(ex));
      }
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, [id]);
  // Live-sync so signatures / status added from the mobile app appear here.
  useLivePoll(reload, 5000, true);

  return (
    <div className="min-h-screen" data-testid="breakdown-detail-page">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => nav(-1)}
          className="btn btn-sm btn-ghost"
          data-testid="breakdown-page-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {bd && (
          <div className="flex items-center gap-2">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => downloadReportPdf(bd, "Breakdown")}
              data-testid="breakdown-page-pdf"
              title="Download PDF"
            >
              <FileDown className="w-4 h-4" /> PDF
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setEmailOpen(true)}
              data-testid="breakdown-page-email"
              title="Email report"
            >
              <Mail className="w-4 h-4" /> Email
            </button>
          </div>
        )}
      </div>

      <div className="px-5 pb-8">
        {busy && !bd && (
          <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading breakdown…
          </div>
        )}
        {err && !bd && (
          <div className="pill pill-red !text-sm !py-2 !px-3 justify-start">
            <AlertTriangle className="w-3.5 h-3.5" /> {err}
          </div>
        )}
        {bd && <ReportDocumentView type="breakdown" item={bd} />}
      </div>

      {emailOpen && bd && (
        <EmailModal open kind="Breakdown" item={bd} onClose={() => setEmailOpen(false)} />
      )}
    </div>
  );
}
